import * as React from "react";

import type { ConnectionSummary } from "@ando/core";
import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";

import {
  ConnectAccountDialog,
  FilesystemConnectDialog,
  SpreadsheetConnectDialog,
  type SpreadsheetConnectDialogProps,
  type SpreadsheetSourceMode,
  type StartFlowField,
  type StartFlowResult,
} from "@/features/connectors/components";
import {
  DeploymentModeChooser,
  EnterpriseDbBridgeSetupWizard,
  isEnterpriseDbBridgeAvailable,
  type BridgeConnectorCreateResponse,
  type DeploymentMode,
  type EnterpriseDbDialect,
} from "@/features/connectors/enterprise-db-bridge";

/**
 * Map a ``databaseSpec`` vendor id → the ``EnterpriseDbDialect`` the desktop
 * bridge understands. Returns ``null`` for vendors the bridge can't handle
 * (``sqlite`` — no on-prem story; ``generic_odbc`` — DSN-only, not modelled
 * by the sqlx-backed bridge), leaving those on the cloud path where they
 * already work. The cloud spec vendor ids come from
 * ``features/connectors/wizards/databaseSpec.tsx``; the bridge dialect
 * vocabulary lives in ``enterprise-db-bridge/types.ts::EnterpriseDbDialect``
 * and is mirrored on the Rust side (``apps/desktop/src-tauri/src/db/
 * bridge.rs::Dialect``). Kept module-scope so the branch below stays a
 * one-liner and no per-render allocation happens.
 */
const DB_VENDOR_TO_BRIDGE_DIALECT: Readonly<Record<string, EnterpriseDbDialect>> = {
  postgresql: "postgresql",
  mysql: "mysql",
  sql_server: "mssql",
  oracle: "oracle",
};

// Pull ``openPicker`` and ``onSubmit`` shapes structurally out of the
// SpreadsheetConnectDialog props so this layer never reaches into the
// underscore-private ``_spreadsheet`` folder for type imports.
type PickerOpener = NonNullable<SpreadsheetConnectDialogProps["openPicker"]>;
type ImportSpreadsheetSubmit = SpreadsheetConnectDialogProps["onSubmit"];

/**
 * Top-level "connect" dialog mounts gated on ``selectedVendor !== null``.
 *
 * Renders ONE of:
 *
 *   • ``SpreadsheetConnectDialog`` — when the route is the spreadsheet
 *     capability AND a vendor has been picked. Owns the file-picker
 *     wiring, multi-account chip selector, and source-mode gating.
 *   • ``ConnectAccountDialog``    — for every other capability. Owns the
 *     OAuth-redirect / credentials-form discriminated flow.
 *
 * Both share the same ``selectedVendor`` slot (the route's single source
 * of truth) so they can never mount simultaneously. The layer simply
 * dispatches based on ``isSpreadsheet`` — no state lives here.
 *
 * Extracted from ``$capability.tsx`` on May 28, 2026.
 */
export interface ConnectorDialogsLayerProps {
  readonly isSpreadsheet: boolean;
  /** Filesystem capability (DESKTOP-ONLY) — uses its own custom dialog. */
  readonly isFilesystem: boolean;
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly selectedVendor: string | null;
  readonly onClose: () => void;
  // ── Spreadsheet-only ──
  readonly spreadsheetSourceMode: SpreadsheetSourceMode | null;
  readonly providerConnected: boolean;
  readonly cloudConnections: readonly ConnectionSummary[];
  readonly openPicker: PickerOpener;
  readonly onNavigateToDriveSetup: () => void;
  readonly onImportSpreadsheet: ImportSpreadsheetSubmit;
  readonly onImportSuccess: (connectorId: string) => void;
  // ── Filesystem-only (desktop) ──
  readonly onFilesystemSuccess: (connectorId: string) => void;
  // ── Enterprise DB bridge (desktop, database capability only) ──
  /**
   * Fired once the BE ``/enterprise-connectors/bridge`` row is created —
   * the parent invalidates the per-capability list so the fresh bridge
   * connector appears in "Account collegati" and closes the dialog. Same
   * refresh semantics as the cloud ``onSubmitCredentials`` path (which
   * invalidates via ``useSubmitCredentials``), so the two paths converge
   * on the same UX from the user's point of view.
   */
  readonly onBridgeRegistered: (row: BridgeConnectorCreateResponse) => void;
  // ── Generic connect-account flow ──
  readonly onStartFlow: (vendorId: string) => Promise<StartFlowResult>;
  readonly onSubmitCredentials: (vendor: string, values: Record<string, string>) => Promise<void>;
  /** Pre-fetched credentials schema — the ConnectAccountDialog opens straight
   *  on its form (OAuth redirects before this layer mounts the dialog). */
  readonly connectInitialFields?: readonly StartFlowField[] | null;
}

export const ConnectorDialogsLayer: React.FC<ConnectorDialogsLayerProps> = ({
  isSpreadsheet,
  isFilesystem,
  spec,
  selectedVendor,
  onClose,
  spreadsheetSourceMode,
  providerConnected,
  cloudConnections,
  openPicker,
  onNavigateToDriveSetup,
  onImportSpreadsheet,
  onImportSuccess,
  onFilesystemSuccess,
  onBridgeRegistered,
  onStartFlow,
  onSubmitCredentials,
  connectInitialFields = null,
}) => {
  const dialogOpen = selectedVendor !== null;

  // ── Deployment-mode gate (desktop, database capability only) ────────
  // On desktop, a DB vendor with a bridge-capable dialect can be reached
  // either directly from the Ando cloud OR through this Tauri shell.
  // Neither path is safe to auto-pick (LAN-only DBs need the bridge;
  // managed public DBs prefer the cloud) so the click first opens
  // ``DeploymentModeChooser``; only after the user picks do we render
  // the corresponding wizard. On web this state stays ``null`` forever
  // (``bridgeDialect`` is null and the chooser branch is skipped),
  // preserving the historical zero-question cloud flow. Reset whenever
  // the vendor selection is torn down so a subsequent open re-asks.
  const [chosenMode, setChosenMode] = React.useState<DeploymentMode | null>(
    null,
  );
  React.useEffect(() => {
    if (selectedVendor === null) setChosenMode(null);
  }, [selectedVendor]);

  if (isFilesystem && selectedVendor !== null) {
    return (
      <FilesystemConnectDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        spec={spec}
        onSuccess={onFilesystemSuccess}
      />
    );
  }

  // ── Enterprise DB bridge (desktop only) ─────────────────────────────
  // The database capability has two mount points depending on the runtime:
  //   • Desktop (Tauri) + a vendor the bridge understands → the user is
  //     first asked cloud-vs-bridge via ``DeploymentModeChooser``; on
  //     ``"bridge"`` we open the on-prem ``EnterpriseDbBridgeSetupWizard``
  //     (creds in the OS keychain via Rust `keyring`, opaque handle to the cloud — design
  //     doc §3); on ``"cloud"`` we fall through to the same
  //     ``ConnectAccountDialog`` path the web build uses.
  //   • Web build, or a vendor the bridge can't handle (``sqlite``,
  //     ``generic_odbc``) → fall through to the cloud ``ConnectAccountDialog``
  //     path below, unchanged — the chooser is not rendered.
  // The dispatch is the ONE branch point for this switch — no per-vendor
  // ``if (isTauri)`` scattered across the six database cards.
  const bridgeDialect =
    spec.capability === "database" &&
    selectedVendor !== null &&
    isEnterpriseDbBridgeAvailable()
      ? (DB_VENDOR_TO_BRIDGE_DIALECT[selectedVendor] ?? null)
      : null;
  if (bridgeDialect !== null && chosenMode === null) {
    return (
      <DeploymentModeChooser
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        onSelect={setChosenMode}
      />
    );
  }
  if (bridgeDialect !== null && chosenMode === "bridge") {
    return (
      <EnterpriseDbBridgeSetupWizard
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        initialDialect={bridgeDialect}
        onRegistered={onBridgeRegistered}
      />
    );
  }
  // ``chosenMode === "cloud"`` (or bridgeDialect null on web / unsupported
  // dialect) falls through to the standard cloud dialog below.

  if (isSpreadsheet && selectedVendor !== null && spreadsheetSourceMode !== null) {
    return (
      <SpreadsheetConnectDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        spec={spec}
        sourceMode={spreadsheetSourceMode}
        providerConnected={providerConnected}
        onNavigateToDriveSetup={onNavigateToDriveSetup}
        onSubmit={onImportSpreadsheet}
        onSuccess={onImportSuccess}
        cloudConnections={cloudConnections}
        openPicker={openPicker}
      />
    );
  }

  return (
    <ConnectAccountDialog
      open={dialogOpen}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      spec={spec}
      vendorId={selectedVendor ?? ""}
      onStartFlow={onStartFlow}
      onSubmitCredentials={onSubmitCredentials}
      openInForm
      initialFields={connectInitialFields}
    />
  );
};
ConnectorDialogsLayer.displayName = "ConnectorDialogsLayer";
